import { useCallback, useEffect, useRef, useState } from "react";
import apiClient from "../../core/config/api";
import "./integration.css";
import "./api_access.css";
// Pagination styles, shared with Verification Logs.
import "./email_verify.css";

// The API page: the account's API key, today's usage, the plan's rate limit,
// the base URL and the log of API calls (backend: /account/re/api-usage and
// /account/re/api-logs; calls are recorded in app/services/api_request_log.py).
// Lives at /api-access - nginx sends /api itself to the backend.

const LOG_PAGE_SIZE = 10;
const REFRESH_MS = 30_000;

type RateLimit = { limit: number | null; used: number; window: string };
type ApiUsage = { requests_today: number; rate_limit: RateLimit; base_url: string };
type ApiLog = {
    id: number;
    created_at: string | null;
    method: string;
    endpoint: string;
    status_code: number;
    status_label: string;
    credits: number | null;
    response_ms: number;
};

type ApiError = { response?: { data?: { message?: string; detail?: string } } };

const errorMessage = (err: unknown, fallback: string) => {
    const data = (err as ApiError)?.response?.data;
    return data?.message || data?.detail || fallback;
};

const localMidnight = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
};

const formatTimestamp = (iso: string | null) =>
    iso
        ? new Date(iso).toLocaleString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
              hour: "numeric",
              minute: "2-digit",
          })
        : "—";

const statusClass = (code: number) => {
    if (code >= 200 && code < 300) return "is-success";
    if (code === 429) return "is-limited";
    if (code >= 500) return "is-error";
    return "is-client-error";
};

// "/request/" -> "/request"; the base URL already carries the API prefix.
const displayEndpoint = (method: string, endpoint: string) =>
    `${method} ${endpoint.length > 1 ? endpoint.replace(/\/+$/, "") : endpoint}`;

const ApiAccess = () => {
    const [apiKey, setApiKey] = useState("");
    const [showKey, setShowKey] = useState(false);
    const [copied, setCopied] = useState(false);
    const [loading, setLoading] = useState(true);
    const [regenerating, setRegenerating] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [usage, setUsage] = useState<ApiUsage | null>(null);
    const [baseUrlCopied, setBaseUrlCopied] = useState(false);

    const [logs, setLogs] = useState<ApiLog[]>([]);
    const [total, setTotal] = useState(0);
    const [logsLoading, setLogsLoading] = useState(true);
    const [logsError, setLogsError] = useState<string | null>(null);
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [goToPageInput, setGoToPageInput] = useState("");
    const logsRequest = useRef(0);

    useEffect(() => {
        const fetchApiKey = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await apiClient.get("/account/re/api-key");
                setApiKey(response?.data?.api_key || "");
            } catch (err: unknown) {
                setError(errorMessage(err, "Failed to load API key"));
            } finally {
                setLoading(false);
            }
        };

        fetchApiKey();
    }, []);

    const fetchUsage = useCallback(async () => {
        try {
            const { data } = await apiClient.get<ApiUsage>("/account/re/api-usage", {
                params: { since: localMidnight() },
            });
            setUsage(data);
        } catch {
            // The tiles show "—" until it loads.
        }
    }, []);

    const fetchLogs = useCallback(
        async (silent = false) => {
            const requestId = ++logsRequest.current;
            if (!silent) setLogsLoading(true);
            try {
                const { data } = await apiClient.get("/account/re/api-logs", {
                    params: { page: currentPage, page_size: LOG_PAGE_SIZE, search: search || undefined },
                });
                if (requestId !== logsRequest.current) return;
                setLogs(data?.items || []);
                setTotal(data?.total || 0);
                setLogsError(null);
            } catch (err: unknown) {
                if (requestId !== logsRequest.current) return;
                if (!silent) setLogsError(errorMessage(err, "Failed to load API logs"));
            } finally {
                if (requestId === logsRequest.current) setLogsLoading(false);
            }
        },
        [currentPage, search]
    );

    useEffect(() => {
        fetchUsage();
        const timer = window.setInterval(fetchUsage, REFRESH_MS);
        return () => window.clearInterval(timer);
    }, [fetchUsage]);

    useEffect(() => {
        fetchLogs();
        const timer = window.setInterval(() => fetchLogs(true), REFRESH_MS);
        return () => window.clearInterval(timer);
    }, [fetchLogs]);

    // Search as you type, once typing pauses.
    useEffect(() => {
        const timer = window.setTimeout(() => {
            const next = searchInput.trim();
            if (next !== search) {
                setSearch(next);
                setCurrentPage(1);
                setGoToPageInput("");
            }
        }, 400);
        return () => window.clearTimeout(timer);
    }, [searchInput, search]);

    const generateKey = async () => {
        if (regenerating) return;
        setRegenerating(true);
        setError(null);
        try {
            const response = await apiClient.post("/account/re/api-key/regenerate");
            setApiKey(response?.data?.api_key || "");
            setShowKey(false);
            setCopied(false);
        } catch (err: unknown) {
            setError(errorMessage(err, "Failed to generate API key"));
        } finally {
            setRegenerating(false);
        }
    };

    const handleCopy = async () => {
        if (!apiKey) return;
        await navigator.clipboard.writeText(apiKey);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
        // Feeds the HubSpot "API Key Copied" event; never affects the copy itself.
        apiClient.post("/account/re/api-key/copied").catch(() => {});
    };

    const handleCopyBaseUrl = async () => {
        if (!usage?.base_url) return;
        await navigator.clipboard.writeText(usage.base_url);
        setBaseUrlCopied(true);
        setTimeout(() => setBaseUrlCopied(false), 1500);
    };

    const displayValue = apiKey ? (showKey ? apiKey : "•".repeat(Math.min(apiKey.length, 72))) : "";
    const hasKey = Boolean(apiKey);

    const limit = usage?.rate_limit?.limit ?? null;
    const used = usage?.rate_limit?.used ?? 0;
    const usedPercent = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
    // Shows the path only (/api/v1/api-email-verifier); copy gives the full URL.
    const baseUrlLabel = usage?.base_url ? usage.base_url.replace(/^https?:\/\/[^/]+/, "") || "/" : "—";

    const totalPages = Math.max(1, Math.ceil(total / LOG_PAGE_SIZE));
    const page = Math.min(currentPage, totalPages);

    const handlePageChange = (next: number) => {
        if (next >= 1 && next <= totalPages) {
            setCurrentPage(next);
        }
    };

    const handleGoToPage = () => {
        const next = parseInt(goToPageInput);
        if (!isNaN(next) && next >= 1 && next <= totalPages) {
            handlePageChange(next);
            setGoToPageInput(String(next));
        }
    };

    const renderPageNumbers = () => {
        const pages = [] as (number | string)[];
        const maxVisiblePages = 5;

        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else if (page <= 3) {
            pages.push(1, 2, 3, "...", totalPages);
        } else if (page >= totalPages - 2) {
            pages.push(1, "...", totalPages - 2, totalPages - 1, totalPages);
        } else {
            pages.push(1, "...", page, "...", totalPages);
        }

        return pages;
    };

    return (
        <div className="integration-page api-access-page">
            <h1>API</h1>
            <p className="integration-subtitle">
                Connect ReachStream Email Verifier with your applications and automate email verification at scale.
            </p>

            <div className="integration-section">
                <div className="section-heading">
                    <img src="/images/api-key.png" alt="" />
                    <span>API Authentication</span>
                </div>

                <div className="api-key-card api-auth-card">
                    <div className="api-key-row">
                        <div className={`api-key-bar${hasKey ? " active" : ""}`}>
                            <input
                                type="text"
                                readOnly
                                value={loading ? "Loading..." : displayValue}
                                className={`api-key-input${hasKey ? " active" : ""}`}
                                aria-label="API key"
                            />
                            <button
                                type="button"
                                className="bar-icon-btn"
                                onClick={() => setShowKey((s) => !s)}
                                disabled={!apiKey}
                                aria-label={showKey ? "Hide API key" : "Show API key"}
                            >
                                <img
                                    src={
                                        hasKey
                                            ? showKey
                                                ? "/images/integration-hide.png"
                                                : "/images/integration-view.png"
                                            : "/images/grey-eye.png"
                                    }
                                    alt=""
                                />
                            </button>
                            <span className="bar-divider" />
                            <button
                                type="button"
                                className={`bar-icon-btn copy-btn${hasKey ? " active" : ""}`}
                                onClick={handleCopy}
                                disabled={!apiKey}
                            >
                                <img src={hasKey ? "/images/integration-copy-icon-active.png" : "/images/integration-copy-icon-inactive.png"} alt="" />
                                <span>{copied ? "Copied" : "Copy"}</span>
                            </button>
                        </div>
                        <button
                            type="button"
                            className="generate-key-btn"
                            onClick={generateKey}
                            disabled={loading || regenerating}
                        >
                            {regenerating ? (
                                <div className="spinner-border spinner-border-sm" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            ) : hasKey ? (
                                "Regenerate API Key"
                            ) : (
                                "Generate API Key"
                            )}
                        </button>
                    </div>
                    {error ? (
                        <p className="api-key-helper" style={{ color: "#B02A37" }}>{error}</p>
                    ) : (
                        <p className="api-key-helper">Your API key authenticates every request. Keep it secure and never share it publicly.</p>
                    )}

                    <div className="api-stats">
                        <div className="api-stat">
                            <span className="api-stat-label">Requests today</span>
                            <span className="api-stat-value">
                                {usage ? usage.requests_today.toLocaleString("en-US") : "—"}
                            </span>
                        </div>

                        <div className="api-stat">
                            <span className="api-stat-label">Rate limit</span>
                            {usage && limit !== null ? (
                                <span className="api-stat-meter">
                                    <span className="api-stat-value">
                                        {used.toLocaleString("en-US")} / {limit.toLocaleString("en-US")}
                                        <small> per {usage.rate_limit.window || "minute"}</small>
                                    </span>
                                    <span
                                        className="api-stat-bar"
                                        role="progressbar"
                                        aria-valuemin={0}
                                        aria-valuemax={limit}
                                        aria-valuenow={used}
                                        aria-label="Requests used this minute"
                                    >
                                        <span style={{ width: `${usedPercent}%` }} />
                                    </span>
                                </span>
                            ) : (
                                <span className="api-stat-value">{usage ? "Unlimited" : "—"}</span>
                            )}
                        </div>

                        <div className="api-stat">
                            <span className="api-stat-label">Base URL</span>
                            <span className="api-stat-url">
                                <span className="api-stat-value is-url" title={usage?.base_url || ""}>
                                    {baseUrlLabel}
                                </span>
                                {usage?.base_url && (
                                    <button
                                        type="button"
                                        className="api-url-copy"
                                        onClick={handleCopyBaseUrl}
                                        aria-label="Copy base URL"
                                        title={baseUrlCopied ? "Copied" : "Copy base URL"}
                                    >
                                        <img
                                            src={baseUrlCopied ? "/images/integration-copy-icon-inactive.png" : "/images/integration-copy-icon-active.png"}
                                            alt=""
                                        />
                                    </button>
                                )}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="integration-section">
                <div className="api-logs-card">
                    <div className="api-logs-head">
                        <h2>API Logs</h2>
                        <label className="api-logs-search">
                            <img src="/images/search-icon.png" alt="" />
                            <input
                                type="search"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                placeholder="Search by endpoint or status..."
                                aria-label="Search API logs"
                                maxLength={100}
                            />
                        </label>
                    </div>

                    <div className="api-logs-table-wrap">
                        <table className="api-logs-table">
                            <thead>
                                <tr>
                                    <th>Timestamp</th>
                                    <th>Endpoint</th>
                                    <th>Status</th>
                                    <th>Credits</th>
                                    <th>Response time</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logsLoading && logs.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="api-logs-empty">Loading...</td>
                                    </tr>
                                ) : logsError ? (
                                    <tr>
                                        <td colSpan={5} className="api-logs-empty is-error">{logsError}</td>
                                    </tr>
                                ) : logs.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="api-logs-empty">
                                            {search
                                                ? "No API calls match your search."
                                                : "No API calls yet. Requests made with your API key will show up here."}
                                        </td>
                                    </tr>
                                ) : (
                                    logs.map((row) => (
                                        <tr key={row.id}>
                                            <td>{formatTimestamp(row.created_at)}</td>
                                            <td>{displayEndpoint(row.method, row.endpoint)}</td>
                                            <td>
                                                <span className={`api-status-pill ${statusClass(row.status_code)}`}>
                                                    {row.status_code} {row.status_label}
                                                </span>
                                            </td>
                                            <td>{row.credits ? row.credits.toLocaleString("en-US") : "—"}</td>
                                            <td>{row.response_ms.toLocaleString("en-US")} ms</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {totalPages > 1 && (
                        <div className="pagination-container">
                            <button
                                className="pagination-arrow"
                                onClick={() => handlePageChange(page - 1)}
                                disabled={page === 1}
                            >
                                &lt;
                            </button>

                            {renderPageNumbers().map((item, index) => (
                                item === "..." ? (
                                    <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>
                                ) : (
                                    <button
                                        key={item}
                                        className={`pagination-number ${page === item ? "active" : ""}`}
                                        onClick={() => handlePageChange(item as number)}
                                    >
                                        {item}
                                    </button>
                                )
                            ))}
                            <button
                                className="pagination-arrow"
                                onClick={() => handlePageChange(page + 1)}
                                disabled={page === totalPages}
                            >
                                &gt;
                            </button>

                            <div className="go-to-page">
                                <span>Go to page</span>
                                <input
                                    type="number"
                                    min={1}
                                    max={totalPages}
                                    value={goToPageInput}
                                    onChange={(e) => setGoToPageInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            handleGoToPage();
                                        }
                                    }}
                                    onBlur={handleGoToPage}
                                    className="page-input"
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ApiAccess;
